import { DetailIcon } from './DetailModalControls';
import { useEmployeeDetailFilters } from './EmployeeDetailFilters';
import { useState } from 'react';
import { money, recordMetrics, sumMetrics, variancePct } from './gaReporting';

export function MetricCells({ row }) {
    return <>
        <td>{money(row.actual)}</td><td>{money(row.aop)}</td><td>{money(row.py)}</td>
        {[row.aop, row.py].map((base, i) => <td key={i} style={{ color: row.actual <= base ? '#15803d' : '#dc2626' }}>
            <strong>{money(row.actual - base)}</strong><br /><small>{variancePct(row.actual, base)}</small>
        </td>)}
    </>;
}

export function MetricHeaders() {
    return <><th>Actual/Forecast</th><th>AOP</th><th>Prior Year</th><th>Var AOP and %</th><th>Var PY and %</th></>;
}

export default function GARecordDetails({
    records, source, category, months, currentYear, priorYear, loading, error,
    expense = null, onSelectExpense, showItems = false, onShowItems,
}) {
    const [search, setSearch] = useState('');
    const isHc = source === 'hc';
    const scoped = (records || []).filter(r => !category || category === 'Total' || (r.category || 'Unspecified') === category);
    const employeeFilters = useEmployeeDetailFilters(scoped, source);
    const valued = employeeFilters.records.map(r => ({
        ...r, ...recordMetrics(r, months, currentYear, priorYear),
        displayName: (r._source || source) === 'hc' ? (r.headcount_name_actual || r.headcount_name_aop || 'Unnamed employee') : (r.name || 'Unnamed vendor'),
    }));
    const filtered = valued.filter(r => (!expense || (r.sub_category || 'Unspecified') === expense)
        && [r.displayName, r.category, r.sub_category, r.status].some(v => String(v || '').toLowerCase().includes(search.toLowerCase())));
    const grouped = source === 'nonhc' && !expense && !showItems;
    const groups = new Map();
    if (grouped) filtered.forEach(r => {
        const name = r.sub_category || 'Unspecified';
        groups.set(name, [...(groups.get(name) || []), r]);
    });
    const allRows = grouped ? [...groups].map(([name, items]) => ({ id: name, displayName: name, ...sumMetrics(items) })) : filtered;
    // Hide rows with no real Actual spend in the current scope — an
    // employee/vendor budgeted in AOP but never actually touched in the
    // selected month/quarter/filter is just noise here, not a real cost
    // driver to review. Checked against the rounded-to-$1K value (what
    // money() actually displays), not the raw number, so a tiny nonzero
    // actual that still prints as "$0K" doesn't sneak past this filter.
    const rows = allRows.filter(r => Math.round(Math.abs(r.actual || 0) / 1000) !== 0);
    rows.sort((a, b) => b.actual - a.actual);
    if (error) return <p role="alert">{error}</p>;
    if (loading) return <p role="status">Loading details...</p>;
    return <>
        {!priorYear && <p>Prior-year actuals; AOP and earlier-year comparisons are unavailable.</p>}
        <div className="esd-record-toolbar">
            {employeeFilters.controls}
            <label className="esd-record-search"><DetailIcon name="search" />
                <input className="esd-drill-search" aria-label="Search details" placeholder={isHc ? 'Search employees...' : 'Search names or categories...'} value={search} onChange={e => setSearch(e.target.value)} />
            </label>
        </div>
        <div className="esd-table-wrap"><table className="esd-table esd-table-static">
            <thead><tr><th>{grouped ? 'Expense Category' : isHc ? 'Employee' : source === 'total' ? 'Employee / Vendor' : 'Vendor / Item'}</th><th>Department / Category</th><MetricHeaders /></tr></thead>
            <tbody>{rows.map(r => <tr key={`${r._source || source}-${r.id}`}>
                <td>{grouped ? <button type="button" className="esd-detail-link" onClick={() => onSelectExpense(r.displayName)}>{r.displayName}</button> : r.displayName}</td>
                <td>{r.category || (grouped ? 'All in scope' : 'Unspecified')}</td><MetricCells row={r} />
            </tr>)}{!rows.length && <tr><td colSpan={7}>No records in this scope.</td></tr>}</tbody>
            <tfoot><tr><td colSpan={2}>{grouped ? <button type="button" className="esd-detail-link" onClick={() => onShowItems(true)}>Total — view vendors</button> : <strong>Total{search ? ' (search results)' : ''}</strong>}</td><MetricCells row={sumMetrics(filtered)} /></tr></tfoot>
        </table></div>
    </>;
}
